import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { BudgetServiceOrderTerciary } from '../../../models/BudgetServiceOrderTerciary';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { BudgetServiceOrdersService } from '../../../services/budget-service-orders.service';
import {
  ServiceOrderWorkSection,
  ServiceOrderWorkSectionItem,
  ServiceOrderWorkSectionService,
  ServiceOrderWorkSectionsService
} from '../../../services/service-order-work-sections.service';
import { BudgetCollaborationService } from '../../../services/budget-collaboration.service';
import { ServiceOrdersService } from '../../../services/service-orders.service';
import { ContractsService } from '../../../services/contracts.service';
import { ServiceOrder } from '../../../models/ServiceOrder';
import { B2bContractDetail } from '../../../models/Contract';
import {
  datetimeLocalToRecordedAtIso,
  defaultDatetimeLocalValue
} from '../../../shared/utils/action-recorded-at.util';
import { valorPorExtenso } from '../../../shared/utils/valor-por-extenso.util';

type DetailTab = 'itens' | 'comentarios' | 'evidencias';

/** Dados fixos da empresa no cabeçalho da impressão (layout fatura Loca9). */
const PRINT_COMPANY = {
  razaoSocial: 'LOCA9 MOTOS LOCACAO DE MOTOCICLETAS E AUTOMOVEIS LTDA',
  cnpj: '37.724.837/0001-93',
  endereco: 'Av. Corifeu de Azevedo Marques, 53',
  bairroCidadeCep: 'Butantã - Butantã-SP - 05581-000',
  contato: '11960201000 - contato@loca9motos.com.br'
};

@Component({
  selector: 'app-budget-service-order-detail',
  templateUrl: './budget-service-order-detail.component.html',
  styleUrls: ['./budget-service-order-detail.component.css']
})
export class BudgetServiceOrderDetailComponent implements OnInit {
  budgetId = 0;
  loading = true;
  detailTab: DetailTab = 'itens';

  budget: BudgetServiceOrderTerciary | null = null;
  workSection: ServiceOrderWorkSection | null = null;
  services: ServiceOrderWorkSectionService[] = [];
  items: ServiceOrderWorkSectionItem[] = [];
  serviceOrder: ServiceOrder | null = null;
  /** Contrato B2B da OS — fonte dos dados do cliente na impressão. */
  b2bContract: B2bContractDetail | null = null;

  approving = false;
  confirming = false;
  revising = false;
  mirroring = false;
  cancelling = false;

  showApproveRecordedAtModal = false;
  approveRecordedAt = '';
  approveRecordedAtError = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService,
    private budgetService: BudgetServiceOrdersService,
    private workSectionsService: ServiceOrderWorkSectionsService,
    private serviceOrdersService: ServiceOrdersService,
    private contractsService: ContractsService,
    private budgetCollaboration: BudgetCollaborationService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.budgetId = Number(params.get('id') || 0);
      if (!this.budgetId) {
        this.errorService.showErrorMessage('Orçamento inválido.');
        this.goBack();
        return;
      }
      this.applyTabFromQuery();
      this.loadDetail();
    });

    this.route.queryParamMap.subscribe(() => this.applyTabFromQuery());
  }

  private applyTabFromQuery(): void {
    const tab = String(this.route.snapshot.queryParamMap.get('tab') || '').toLowerCase();
    if (tab === 'comentarios' || tab === 'evidencias' || tab === 'itens') {
      this.detailTab = tab;
    }
  }

  selectTab(tab: DetailTab): void {
    this.detailTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === 'itens' ? null : tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  loadDetail(): void {
    this.loading = true;
    this.budgetService.getById(this.budgetId).subscribe({
      next: budget => {
        this.budget = budget;
        const workSectionId = Number(budget.serviceOrderWorkSectionId || 0);
        if (!workSectionId) {
          this.loading = false;
          this.errorService.showErrorMessage('Orçamento sem seção de trabalho vinculada.');
          return;
        }
        this.loadWorkSectionData(workSectionId);
      },
      error: () => {
        this.loading = false;
        this.goBack();
      }
    });
  }

  private loadWorkSectionData(workSectionId: number): void {
    this.b2bContract = null;
    forkJoin({
      workSection: this.workSectionsService.getById(workSectionId).pipe(
        catchError(() => of(null as ServiceOrderWorkSection | null))
      ),
      services: this.workSectionsService.getServicesByWorkSectionId(workSectionId).pipe(
        catchError(() => of([] as ServiceOrderWorkSectionService[]))
      ),
      items: this.workSectionsService.getItemsByWorkSectionId(workSectionId).pipe(
        catchError(() => of([] as ServiceOrderWorkSectionItem[]))
      )
    }).subscribe(({ workSection, services, items }) => {
      this.workSection = workSection;
      this.services = services || [];
      this.items = items || [];

      const serviceOrderId = Number(workSection?.serviceOrderId || 0);
      if (serviceOrderId) {
        this.serviceOrdersService.getById(serviceOrderId).pipe(
          catchError(() => of(null as ServiceOrder | null)),
          switchMap(os => {
            this.serviceOrder = os;
            const contractId = Number(os?.b2bContractId || os?.contratoId || 0);
            if (!contractId) {
              return of(null as B2bContractDetail | null);
            }
            return this.contractsService.getB2bContractById(contractId).pipe(
              catchError(() => of(null as B2bContractDetail | null))
            );
          })
        ).subscribe(contract => {
          this.b2bContract = contract;
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/b2b/budget-service-orders']);
  }

  get executionType(): 'Base' | 'Parceiro' {
    const providerId = Number(this.workSection?.externalServiceProviderId || 0);
    if (providerId > 0 || this.workSection?.externalServiceProvider) {
      return 'Parceiro';
    }
    return 'Base';
  }

  get providerName(): string | null {
    const p = this.workSection?.externalServiceProvider;
    if (!p) return null;
    return p.tradeName || p.companyName || null;
  }

  get canApprove(): boolean {
    return this.canActOnBudget && !this.budget?.clientApproved && !this.isApprovedStatus && !this.isCompletedStatus && !this.isCancelled;
  }

  get canRequestRevision(): boolean {
    return this.canActOnBudget && !this.budget?.clientApproved && !this.isApprovedStatus && !this.isCompletedStatus && !this.isInRevision && !this.isCancelled;
  }

  get canConfirmService(): boolean {
    return this.canActOnBudget && (this.isApprovedStatus || !!this.budget?.clientApproved) && !this.isCompletedStatus && !this.isCancelled;
  }

  get canEdit(): boolean {
    return this.canActOnBudget && !this.isCompletedStatus && !this.isCancelled;
  }

  get canCancelBudget(): boolean {
    return this.canActOnBudget && !this.isCompletedStatus && !this.isCancelled;
  }

  private get canActOnBudget(): boolean {
    return !!this.budget?.id;
  }

  private get statusLower(): string {
    return (this.budget?.status || '').toLowerCase();
  }

  private get isApprovedStatus(): boolean {
    return this.statusLower === 'aprovado';
  }

  private get isCompletedStatus(): boolean {
    return this.statusLower === 'serviço realizado' || this.statusLower === 'servico realizado'
      || this.statusLower === 'concluído' || this.statusLower === 'concluido';
  }

  private get isInRevision(): boolean {
    return this.statusLower === 'em revisão' || this.statusLower === 'em revisao';
  }

  private get isCancelled(): boolean {
    return this.statusLower === 'cancelado' || this.statusLower === 'cancelada';
  }

  editBudget(): void {
    if (!this.budget?.id || !this.canEdit) return;
    this.router.navigate(['/b2b/budget-service-orders', this.budget.id, 'editar']);
  }

  cancelBudget(): void {
    if (!this.budget?.id) return;

    this.confirmationService.confirm({
      title: 'Cancelar orçamento e OS',
      message: `Cancelar o orçamento #${this.budget.id}? A ordem de serviço vinculada também será cancelada. Esta ação não pode ser desfeita.`,
      confirmText: 'Cancelar orçamento',
      cancelText: 'Voltar',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-secondary',
      type: 'danger'
    }).subscribe(confirmed => {
      if (!confirmed || !this.budget?.id) return;

      this.cancelling = true;
      this.budgetService.cancel(this.budget.id).subscribe({
        next: () => {
          this.cancelling = false;
          this.loadDetail();
        },
        error: () => {
          this.cancelling = false;
        }
      });
    });
  }

  approveBudget(): void {
    if (!this.budget?.id) return;
    if (this.budget.clientApproved) {
      this.errorService.showErrorMessage('Este orçamento já está aprovado.');
      return;
    }

    this.approveRecordedAt = defaultDatetimeLocalValue();
    this.approveRecordedAtError = '';
    this.showApproveRecordedAtModal = true;
  }

  closeApproveRecordedAtModal(): void {
    this.showApproveRecordedAtModal = false;
    this.approveRecordedAt = '';
    this.approveRecordedAtError = '';
  }

  confirmApproveBudget(): void {
    if (!this.budget?.id) return;

    const recordedAt = datetimeLocalToRecordedAtIso(this.approveRecordedAt);
    if (!recordedAt) {
      this.approveRecordedAtError = 'Informe uma data e hora válidas.';
      return;
    }

    this.approveRecordedAtError = '';
    this.showApproveRecordedAtModal = false;
    this.approving = true;

    this.budgetCollaboration.approveAll(this.budget.id, recordedAt).subscribe({
      next: () => {
        this.approving = false;
        this.errorService.showSuccessMessage('Orçamento aprovado.');
        this.loadDetail();
      },
      error: () => {
        this.approving = false;
      }
    });
  }

  confirmService(): void {
    if (!this.budget?.id) return;

    this.confirmationService.confirm({
      title: 'Confirmar serviço',
      message: `Confirmar que o serviço do orçamento #${this.budget.id} foi realizado?`,
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-success',
      cancelButtonClass: 'btn-secondary',
      type: 'question'
    }).subscribe(confirmed => {
      if (!confirmed || !this.budget?.id) return;

      this.confirming = true;
      this.budgetService.update(this.budget.id, {
        serviceOrderWorkSectionId: this.budget.serviceOrderWorkSectionId,
        osNumber: this.budget.osNumber,
        plateVechile: this.budget.plateVechile,
        status: 'Serviço Realizado',
        clientApproved: true
      }).pipe(
        switchMap(updated => {
          const wsId = Number(this.budget?.serviceOrderWorkSectionId || 0);
          if (!wsId) return of(updated);
          return this.updateWorkSectionStatus(wsId, 'Serviço Realizado').pipe(
            switchMap(() => this.workSectionsService.updateItemsPaymentStatus(wsId, 'PENDENTE')),
            map(() => updated)
          );
        })
      ).subscribe({
        next: updated => {
          this.confirming = false;
          this.budget = { ...this.budget!, ...updated };
          if (this.workSection) {
            this.workSection = { ...this.workSection, status: 'Serviço Realizado' };
          }
          this.errorService.showSuccessMessage('Serviço confirmado.');
        },
        error: () => { this.confirming = false; }
      });
    });
  }

  requestRevision(): void {
    if (!this.budget?.id) return;

    this.confirmationService.confirm({
      title: 'Solicitar revisão do orçamento',
      message: `Confirmar que o cliente NÃO aprovou o orçamento #${this.budget.id}? O status será alterado para "Em Revisão".`,
      confirmText: 'Solicitar Revisão',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-warning',
      cancelButtonClass: 'btn-secondary',
      type: 'warning'
    }).subscribe(confirmed => {
      if (!confirmed || !this.budget?.id) return;

      this.revising = true;
      this.budgetService.update(this.budget.id, {
        serviceOrderWorkSectionId: this.budget.serviceOrderWorkSectionId,
        osNumber: this.budget.osNumber,
        plateVechile: this.budget.plateVechile,
        status: 'Em Revisão',
        clientApproved: false
      }).pipe(
        switchMap(updated => {
          const wsId = Number(this.budget?.serviceOrderWorkSectionId || 0);
          if (!wsId) return of(updated);
          return this.updateWorkSectionStatus(wsId, 'Orçamento-Em-Revisão').pipe(map(() => updated));
        })
      ).subscribe({
        next: updated => {
          this.revising = false;
          this.budget = { ...this.budget!, ...updated };
          if (this.workSection) {
            this.workSection = { ...this.workSection, status: 'Orçamento-Em-Revisão' };
          }
        },
        error: () => { this.revising = false; }
      });
    });
  }

  openServiceOrder(): void {
    const id = Number(this.serviceOrder?.id || this.workSection?.serviceOrderId || 0);
    if (!id || !this.budget?.id) return;

    this.mirroring = true;
    this.budgetCollaboration.mirrorApproved(this.budget.id).subscribe({
      next: () => {
        this.mirroring = false;
        this.router.navigate(['/service-orders', id, 'view']);
      },
      error: (err) => {
        this.mirroring = false;
        const msg = err?.message || 'Não foi possível espelhar os itens aprovados na OS.';
        this.errorService.showErrorMessage(msg);
      }
    });
  }

  private updateWorkSectionStatus(workSectionId: number, status: string) {
    return this.workSectionsService.getById(workSectionId).pipe(
      switchMap(section => {
        const payload: any = { status };
        const serviceOrderId = Number(section?.serviceOrderId || 0);
        if (serviceOrderId) payload.serviceOrderId = serviceOrderId;
        if (section?.description) payload.description = section.description;
        if (section?.externalServiceProviderId != null) {
          payload.externalServiceProviderId = section.externalServiceProviderId;
        }
        return this.workSectionsService.update(workSectionId, payload).pipe(catchError(() => of(null)));
      }),
      catchError(() => of(null))
    );
  }

  /** Impressão no layout da fatura Loca9 (cabeçalho, cliente, veículo, itens, obs, totais). */
  imprimirOrcamento(): void {
    if (!this.budget) {
      this.errorService.showErrorMessage('Orçamento ainda não carregado.');
      return;
    }

    const money = (v: number | null | undefined) =>
      Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const qty = (v: number | null | undefined) =>
      Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    const dateOnly = (value: string | Date | null | undefined) => {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('pt-BR');
    };
    const timeOnly = (value: string | Date | null | undefined) => {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };
    const esc = (value: unknown) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const os = this.serviceOrder;
    const contract = this.b2bContract;
    const client = this.resolvePrintClientData(os, contract);
    const placa = this.budget.plateVechile || os?.veiculoPlaca || '';
    const osNumber = this.budget.osNumber || (os?.numeroOS ? `OS-${os.numeroOS}` : `ORC-${this.budget.id}`);
    const dataDoc = dateOnly(this.budget.updatedAt || this.budget.createdAt || new Date().toISOString()) || new Date().toLocaleDateString('pt-BR');
    const contratoLabel =
      client.contratoNumero ||
      os?.contratoNumero ||
      contract?.contractNumber ||
      (contract?.id ? String(contract.id) : '') ||
      (os?.b2bContractId ? String(os.b2bContractId) : '') ||
      '—';

    const rows = [
      ...(this.services || []).map(s => ({
        descricao: s.description || '—',
        qtd: Number(s.quantity || 0),
        unit: Number(s.unitValue || 0),
        total: Number(s.totalValue || 0),
        status: s.approvalStatus || 'Pendente'
      })),
      ...(this.items || []).map(it => ({
        descricao: it.description || '—',
        qtd: Number(it.quantity || 0),
        unit: Number(it.unitValue || 0),
        total: Number(it.totalValue || 0),
        status: it.approvalStatus || 'Pendente'
      }))
    ];

    const subtotal = rows.reduce((acc, r) => acc + r.total, 0);
    const totalAprovadoLinhas = rows
      .filter(r => String(r.status).toLowerCase().startsWith('aprov'))
      .reduce((acc, r) => acc + r.total, 0);
    const total =
      Number(this.workSection?.totalApproved || 0) > 0
        ? Number(this.workSection?.totalApproved)
        : totalAprovadoLinhas > 0
          ? totalAprovadoLinhas
          : subtotal;

    const aprovador =
      this.workSection?.approvedBy ||
      this.budget.updatedBy ||
      (this.budget.clientApproved || this.isApprovedStatus ? 'Cliente / Portal' : '');

    const veiculoLinha = [placa].filter(Boolean).join(' - ') || '—';
    const execucao =
      this.executionType === 'Base'
        ? 'Base'
        : this.providerName || this.budget.externalServiceProviderName || 'Parceiro';

    const obsParts = [
      this.budget.status ? `Status do orçamento: ${this.budget.status}.` : '',
      this.budget.clientApproved ? 'Cliente aprovou o orçamento.' : '',
      aprovador ? `Aprovado/atualizado por: ${aprovador}.` : '',
      execucao ? `Execução: ${execucao}.` : '',
      os?.motivoAbertura ? `Motivo de abertura: ${os.motivoAbertura}.` : '',
      this.workSection?.description ? `Descrição da seção: ${this.workSection.description}.` : ''
    ].filter(Boolean);

    const lineHtml = rows.length
      ? rows
          .map(
            r => `
        <tr>
          <td>${esc(r.descricao)}</td>
          <td class="c">${qty(r.qtd)}</td>
          <td class="r">${money(r.unit)}</td>
          <td class="r">${money(r.total)}</td>
        </tr>`
          )
          .join('')
      : `<tr><td colspan="4" class="c">Nenhum item no orçamento.</td></tr>`;

    const htmlContent = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${esc(osNumber)} - Orçamento</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #111; background: #fff; padding: 12mm 10mm; }
    .doc { max-width: 190mm; margin: 0 auto; }
    .company { text-align: center; line-height: 1.35; margin-bottom: 10px; }
    .company .name { font-weight: 700; font-size: 11pt; text-transform: uppercase; }
    .company .muted { font-size: 9pt; color: #222; }
    .title-bar {
      display: grid;
      grid-template-columns: 1.1fr 2fr 1.1fr;
      gap: 6px;
      align-items: center;
      border: 1px solid #222;
      margin: 10px 0 12px;
      font-weight: 700;
      font-size: 10.5pt;
    }
    .title-bar > div { padding: 8px 10px; }
    .title-bar .mid { text-align: center; border-left: 1px solid #222; border-right: 1px solid #222; text-transform: uppercase; }
    .title-bar .right { text-align: right; }
    .box { border: 1px solid #222; margin-bottom: 10px; }
    .box-title { background: #f2f2f2; border-bottom: 1px solid #222; padding: 5px 8px; font-weight: 700; }
    .box-body { padding: 8px; }
    .client-grid { display: grid; grid-template-columns: 90px 1fr 110px 1fr; gap: 4px 8px; font-size: 9.5pt; }
    .client-grid .lbl { font-weight: 700; }
    .total-extenso {
      display: grid;
      grid-template-columns: 140px 1fr;
      gap: 8px;
      border: 1px solid #222;
      margin-bottom: 10px;
    }
    .total-extenso .valor {
      padding: 10px;
      border-right: 1px solid #222;
      text-align: center;
      font-weight: 700;
      font-size: 12pt;
    }
    .total-extenso .ext {
      padding: 10px;
      font-size: 9.5pt;
    }
    .vehicle-grid {
      display: grid;
      grid-template-columns: 1.6fr repeat(4, 1fr);
      gap: 0;
      border: 1px solid #222;
      margin-bottom: 10px;
      font-size: 9pt;
    }
    .vehicle-grid .cell { padding: 6px 7px; border-right: 1px solid #222; }
    .vehicle-grid .cell:last-child { border-right: none; }
    .vehicle-grid .lbl { display: block; font-weight: 700; margin-bottom: 2px; }
    .vehicle-meta {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      border: 1px solid #222;
      border-top: none;
      margin-top: -10px;
      margin-bottom: 10px;
      font-size: 9pt;
    }
    .vehicle-meta .cell { padding: 6px 7px; border-right: 1px solid #222; }
    .vehicle-meta .cell:last-child { border-right: none; }
    table.items { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 9.5pt; }
    table.items th, table.items td { border: 1px solid #222; padding: 5px 6px; }
    table.items th { background: #f2f2f2; text-align: left; }
    .c { text-align: center; }
    .r { text-align: right; white-space: nowrap; }
    .pay-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      border: 1px solid #222;
      margin-bottom: 10px;
      font-size: 10pt;
    }
    .pay-row > div { padding: 8px 10px; }
    .pay-row .right { border-left: 1px solid #222; }
    .obs { border: 1px solid #222; margin-bottom: 10px; min-height: 70px; }
    .obs .box-title { border-bottom: 1px solid #222; }
    .obs .box-body { white-space: pre-wrap; line-height: 1.4; font-size: 9.5pt; }
    .foot-totals { text-align: right; font-size: 11pt; line-height: 1.6; }
    .foot-totals .total { font-weight: 700; font-size: 12pt; }
    .approval {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 18px;
    }
    .sign {
      border-top: 1px solid #222;
      padding-top: 6px;
      text-align: center;
      font-size: 9pt;
      margin-top: 40px;
    }
    @media print {
      body { padding: 0; }
      .box, .title-bar, table.items, .vehicle-grid, .vehicle-meta, .total-extenso, .pay-row, .obs { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="doc">
    <div class="company">
      <div class="name">${esc(PRINT_COMPANY.razaoSocial)}</div>
      <div class="muted">${esc(PRINT_COMPANY.cnpj)}</div>
      <div class="muted">${esc(PRINT_COMPANY.endereco)}</div>
      <div class="muted">${esc(PRINT_COMPANY.bairroCidadeCep)}</div>
      <div class="muted">${esc(PRINT_COMPANY.contato)}</div>
    </div>

    <div class="title-bar">
      <div>N° ${esc(osNumber)}</div>
      <div class="mid">Orçamento de Serviço</div>
      <div class="right">DATA: ${esc(dataDoc)}</div>
    </div>

    <div class="box">
      <div class="box-title">Dados do Cliente:</div>
      <div class="box-body client-grid">
        <div class="lbl">Nome:</div><div>${esc(client.nome || '—')}</div>
        <div class="lbl">RG/Inscr. Estadual:</div><div>${esc(client.ie || '')}</div>
        <div class="lbl">CPF/CNPJ:</div><div>${esc(client.cnpj || '')}</div>
        <div class="lbl">Endereço:</div><div style="grid-column: 2 / span 3;">${esc(client.endereco || '')}</div>
        <div class="lbl">Cidade/UF:</div><div>${esc(client.cidadeUf || '')}</div>
        <div class="lbl">CEP:</div><div>${esc(client.cep || '')}</div>
        <div class="lbl">Bairro:</div><div>${esc(client.bairro || '')}</div>
        <div class="lbl">Telefone:</div><div>${esc(client.telefone || '')}</div>
        <div class="lbl">Email:</div><div>${esc(client.email || '')}</div>
        <div class="lbl">Contrato:</div><div>${esc(contratoLabel)}</div>
      </div>
    </div>

    <div class="total-extenso">
      <div class="valor">
        <div style="font-size:9pt;font-weight:600;">Total</div>
        ${esc(money(total))}
      </div>
      <div class="ext"><strong>Valor por Extenso:</strong> ${esc(valorPorExtenso(total))}</div>
    </div>

    <div class="vehicle-grid">
      <div class="cell">
        <span class="lbl">Veículo:</span>
        ${esc(veiculoLinha)}
      </div>
      <div class="cell">
        <span class="lbl">Data Saída:</span>
        ${esc(dateOnly(os?.entryDate || os?.dataAbertura))}
        <div><span class="lbl">Hora Saída:</span> ${esc(timeOnly(os?.entryDate || os?.dataAbertura) || '00:00')}</div>
      </div>
      <div class="cell">
        <span class="lbl">Data Retorno:</span>
        ${esc(dateOnly(os?.deadlineDate || os?.dataPrevistaConclusao || os?.endWorkDate))}
        <div><span class="lbl">Hora Retorno:</span> ${esc(timeOnly(os?.deadlineDate || os?.dataPrevistaConclusao || os?.endWorkDate) || '00:00')}</div>
      </div>
      <div class="cell">
        <span class="lbl">N° Locação:</span>
        ${esc(contratoLabel)}
      </div>
      <div class="cell">
        <span class="lbl">Km:</span>
        ${esc(os?.km ?? os?.openingMileage ?? '0')}
        <div><span class="lbl">Data Doc.:</span> ${esc(dataDoc)}</div>
      </div>
    </div>
    <div class="vehicle-meta">
      <div class="cell"><span class="lbl">Agendado:</span> ${os?.isScheduled ? 'Sim' : 'Não informado'}</div>
      <div class="cell"><span class="lbl">Execução:</span> ${esc(execucao)}</div>
      <div class="cell"><span class="lbl">OS sistema:</span> #${esc(os?.id || this.workSection?.serviceOrderId || '—')}</div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th>Descrição dos Itens de Serviços/Produtos:</th>
          <th class="c" style="width:70px;">Qtde.</th>
          <th class="r" style="width:95px;">Preço</th>
          <th class="r" style="width:95px;">Subtotal</th>
        </tr>
      </thead>
      <tbody>${lineHtml}</tbody>
    </table>

    <div class="pay-row">
      <div><strong>Valor</strong><br>${esc(money(total))}</div>
      <div class="right"><strong>Vencimento</strong><br>${esc(dateOnly(os?.deadlineDate || os?.dataPrevistaConclusao) || '—')}</div>
    </div>

    <div class="obs">
      <div class="box-title">Obs:</div>
      <div class="box-body">${esc(obsParts.join(' ') || '—')}</div>
    </div>

    <div class="foot-totals">
      <div>SUBTOTAL:&nbsp;&nbsp;${esc(money(subtotal))}</div>
      <div class="total">TOTAL:&nbsp;&nbsp;${esc(money(total))}</div>
    </div>

    <div class="approval">
      <div class="sign">${esc(aprovador || '_________________________')}<br>Responsável / Aprovador</div>
      <div class="sign">${esc(client.nome || os?.clienteNome || '_________________________')}<br>Cliente / Autorização</div>
    </div>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      this.errorService.showErrorMessage('Permita pop-ups para imprimir o orçamento.');
      return;
    }
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 250);
    };
  }

  /** Monta dados do cliente a partir da OS e do contrato B2B (ex.: PagBank). */
  private resolvePrintClientData(
    os: ServiceOrder | null,
    contract: B2bContractDetail | null
  ): {
    nome: string;
    cnpj: string;
    ie: string;
    endereco: string;
    cidadeUf: string;
    cep: string;
    bairro: string;
    telefone: string;
    email: string;
    contratoNumero: string;
  } {
    const addr = this.parseCompanyAddress(contract?.companyAddress || null);
    const nome =
      (os?.clienteNome || '').trim() ||
      (contract?.companyName || '').trim() ||
      (contract?.customerName || '').trim() ||
      (contract?.contractName || '').trim() ||
      '';

    return {
      nome,
      cnpj: (contract?.cnpj || '').trim(),
      ie: '',
      endereco: addr.endereco,
      cidadeUf: addr.cidadeUf,
      cep: addr.cep,
      bairro: addr.bairro,
      telefone: '',
      email: '',
      contratoNumero: (contract?.contractNumber || os?.contratoNumero || '').trim()
    };
  }

  /**
   * companyAddress pode ser texto livre ("rua - cidade - UF - CEP"),
   * JSON string ou objeto serializado.
   */
  private parseCompanyAddress(raw: string | null): {
    endereco: string;
    cidadeUf: string;
    cep: string;
    bairro: string;
  } {
    const empty = { endereco: '', cidadeUf: '', cep: '', bairro: '' };
    if (!raw || !String(raw).trim()) return empty;

    const text = String(raw).trim();

    // JSON object?
    if (text.startsWith('{')) {
      try {
        const obj = JSON.parse(text) as Record<string, unknown>;
        const street = String(obj['street'] ?? obj['Street'] ?? obj['endereco'] ?? obj['logradouro'] ?? '').trim();
        const number = String(obj['number'] ?? obj['Number'] ?? obj['numero'] ?? '').trim();
        const complement = String(obj['complement'] ?? obj['Complement'] ?? obj['complemento'] ?? '').trim();
        const district = String(obj['district'] ?? obj['District'] ?? obj['bairro'] ?? obj['neighborhood'] ?? '').trim();
        const city = String(obj['city'] ?? obj['City'] ?? obj['cidade'] ?? '').trim();
        const state = String(obj['state'] ?? obj['State'] ?? obj['uf'] ?? '').trim();
        const zip = String(obj['zipCode'] ?? obj['ZipCode'] ?? obj['cep'] ?? '').trim();
        const line = [street, number, complement].filter(Boolean).join(', ');
        return {
          endereco: line,
          cidadeUf: [city, state].filter(Boolean).join(' - '),
          cep: zip,
          bairro: district
        };
      } catch {
        // segue como texto
      }
    }

    // Formato salvo pelo form: "address - city - state - zipCode"
    const parts = text.split(' - ').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 4) {
      const cepCandidate = parts[parts.length - 1];
      const stateCandidate = parts[parts.length - 2];
      const cityCandidate = parts[parts.length - 3];
      const addressPart = parts.slice(0, parts.length - 3).join(' - ');
      const looksLikeCep = /^\d{5}-?\d{3}$/.test(cepCandidate.replace(/\s/g, ''));
      const looksLikeUf = /^[A-Za-z]{2}$/.test(stateCandidate);
      if (looksLikeCep && looksLikeUf) {
        return {
          endereco: addressPart,
          cidadeUf: `${cityCandidate} - ${stateCandidate.toUpperCase()}`,
          cep: cepCandidate,
          bairro: ''
        };
      }
    }

    return { endereco: text, cidadeUf: '', cep: '', bairro: '' };
  }
}
